import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ELEMENT_CATEGORIES, ELEMENT_ITEMS } from '../../data/elementsCatalog';
import ElementTile from './ElementTile';

const GRID_COLUMNS = 2;

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightText = (title, query) => {
  if (!query) return title;
  const safe = escapeRegex(query.trim());
  if (!safe) return title;
  const regex = new RegExp(`(${safe})`, 'ig');
  const parts = title.split(regex);
  return parts.map((part, index) => (
    index % 2 === 1 ? <mark key={`${part}-${index}`}>{part}</mark> : <span key={`${part}-${index}`}>{part}</span>
  ));
};

const ElementsSearchView = ({ query, onActivateItem }) => {
  const [focusIndex, setFocusIndex] = useState(0);
  const tileRefs = useRef([]);
  const tokens = useMemo(() => query.trim().toLowerCase().split(/\s+/).filter(Boolean), [query]);
  const hasQuery = tokens.length >= 2;

  const categoryMap = useMemo(() => {
    const map = new Map();
    ELEMENT_CATEGORIES.forEach((category) => map.set(category.id, category.title.toLowerCase()));
    return map;
  }, []);

  const groupedResults = useMemo(() => {
    if (!hasQuery) return [];
    const scored = ELEMENT_ITEMS.map((item) => {
      const haystack = [
        item.title,
        item.kind,
        item.category,
        item.subcategory,
        categoryMap.get(item.category) || '',
        ...(item.tags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesAll = tokens.every((token) => haystack.includes(token));
      if (!matchesAll) return null;
      const titleLower = item.title.toLowerCase();
      const startsWith = tokens.some((token) => titleLower.startsWith(token)) ? 2 : 0;
      const includes = tokens.some((token) => titleLower.includes(token)) ? 1 : 0;
      return { item, score: startsWith + includes };
    }).filter(Boolean);

    const ordered = scored.sort((a, b) => b.score - a.score).map((entry) => entry.item);
    const groups = new Map();
    ordered.forEach((item) => {
      const key = item.category || 'other';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(item);
    });
    return Array.from(groups.entries()).map(([categoryId, items]) => ({
      categoryId,
      title: ELEMENT_CATEGORIES.find((cat) => cat.id === categoryId)?.title || categoryId,
      items,
    }));
  }, [categoryMap, hasQuery, tokens]);

  useEffect(() => {
    setFocusIndex(0);
  }, [query]);

  useEffect(() => {
    const node = tileRefs.current[focusIndex];
    if (node && node.focus) {
      node.focus();
    }
  }, [focusIndex]);

  const handleGridKeyDown = (event) => {
    const flatCount = groupedResults.reduce((acc, group) => acc + group.items.length, 0);
    if (!flatCount) return;
    let nextIndex = focusIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = Math.min(focusIndex + 1, flatCount - 1);
        break;
      case 'ArrowLeft':
        nextIndex = Math.max(focusIndex - 1, 0);
        break;
      case 'ArrowDown':
        nextIndex = Math.min(focusIndex + GRID_COLUMNS, flatCount - 1);
        break;
      case 'ArrowUp':
        nextIndex = Math.max(focusIndex - GRID_COLUMNS, 0);
        break;
      default:
        return;
    }
    event.preventDefault();
    setFocusIndex(nextIndex);
  };

  let runningIndex = 0;
  return (
    <div className="elements-search-view">
      <div className="elements-category-header">
        <span>Results</span>
      </div>
      <div className="elements-search-meta">Results for: "{query}"</div>
      {!hasQuery ? (
        <div className="elements-empty">
          Type at least 2 characters to search.
        </div>
      ) : groupedResults.length === 0 ? (
        <div className="elements-empty">
          No results. Try searching for shape, line, frame, or arrow.
        </div>
      ) : (
        <div className="elements-search-groups" onKeyDown={handleGridKeyDown}>
          {groupedResults.map((group) => (
            <div key={group.categoryId} className="elements-search-group">
              <div className="elements-search-group-title">{group.title}</div>
              <div className="elements-category-grid elements-grid">
                {group.items.map((item) => {
                  const index = runningIndex;
                  runningIndex += 1;
                  return (
                    <ElementTile
                      key={item.catalogId}
                      item={item}
                      onActivate={onActivateItem}
                      tabIndex={index === focusIndex ? 0 : -1}
                      buttonRef={(node) => {
                        tileRefs.current[index] = node;
                      }}
                      highlightedTitle={highlightText(item.title, tokens[0] || '')}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ElementsSearchView;
